import React, { useRef, useState } from "react";
import type { TypeCardProps, TypeCardContext } from "./CardTypes";
// Import from ./utils exactly as Card.tsx did. This hook deliberately lives in
// its own file (NOT utils.ts) so the test's `jest.mock("../utils")` automock
// does not neutralize it — only onKeyDown/SubComponentContext stay mocked.
import { SubComponentContext, onKeyDown } from "./utils";

type TypeUseCardBehaviorParams = Pick<
  TypeCardProps,
  "disabled" | "href" | "onClick" | "role" | "selected"
>;

/**
 * Shared interactive logic for Card, consumed by both CardStyled and
 * CardTailwind so the two rendering paths cannot drift. Owns the
 * hasSubComponent state, the container/link refs, the SubComponentContext
 * value, and the computed container props (tabIndex, role, handlers, aria-*).
 */
export function useCardBehavior({
  disabled = false,
  href,
  onClick,
  role = "presentation",
  selected,
}: TypeUseCardBehaviorParams) {
  const [hasSubComponent, setHasSubComponent] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const isRoleLink = role === "link";
  const isRolePresentation = role === "presentation";
  const checkedConditions = role === "checkbox" ? selected : undefined;

  const cardContext: TypeCardContext = {
    setHasSubComponent: setHasSubComponent,
    href: href,
    linkRef: linkRef,
  };

  const handleClickConditions: React.MouseEventHandler = (e) =>
    isRoleLink ? linkRef.current?.click() : onClick?.(e);

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) =>
    onKeyDown({ e, href, onClick, ref: containerRef, role });

  const shouldBeInteractive = !isRolePresentation && !disabled;

  const containerProps = {
    tabIndex: isRoleLink || isRolePresentation || disabled ? -1 : 0,
    role: isRoleLink ? undefined : role,
    onClick: shouldBeInteractive ? handleClickConditions : undefined,
    onKeyDown: shouldBeInteractive ? handleKeyDown : undefined,
    "aria-checked": checkedConditions,
    "aria-disabled": disabled && disabled,
  };

  return {
    containerRef,
    linkRef,
    cardContext,
    hasSubComponent,
    isRoleLink,
    SubComponentContext,
    containerProps,
  };
}
